import axios from 'axios';
import { sessionService } from '@/shared/services/session.service';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || '/api/v1';

const commissionsApi = axios.create({
  baseURL: `${API_BASE_URL}/financial/commissions`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación y tenant
commissionsApi.interceptors.request.use(config => {
  config.withCredentials = true;

  // Bug #39 fix (audit Nivel 2 2026-06-03): el endpoint /financial/commissions
  // requería Bearer pero este caller NO lo incluía → 401. Leer token de
  // sessionService (cross-domain compat per session.service.ts:28-33).
  const token = sessionService.getToken();
  if (config.headers && token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  const user = sessionService.getUser();
  if (config.headers && user?.tenantId) {
    config.headers['x-tenant-id'] = user.tenantId;
  }

  return config;
});

export interface CommissionPayment {
  id: string;
  amount: number;
  type: 'advance' | 'partial' | 'final';
  date: string;
  paymentMethod: string;
  reference: string;
  notes?: string;
}

export interface CommissionDocument {
  id: string;
  name: string;
  type: 'receipt' | 'invoice' | 'contract' | 'other';
  url: string;
  date: string;
}

export interface Commission {
  id: string;
  commissionNumber: string;
  agentId: string;
  agentName: string;
  agentEmail: string;
  agentPhone: string;
  propertyId?: string;
  leadId?: string;
  saleReference: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  propertyTitle: string;
  propertyAddress: string;
  saleAmount: number;
  saleDate: string;
  commissionRate: number;
  commissionAmount: number;
  currency: string;
  status: 'pending' | 'partial' | 'paid' | 'cancelled' | 'overdue';
  type: 'sale' | 'rental' | 'referral' | 'bonus' | 'other';
  paidAt?: string;
  paymentMethod?: string;
  dueDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  payments: CommissionPayment[];
  documents: CommissionDocument[];
}

export interface CommissionCreateRequest {
  agentId: string;
  propertyId?: string;
  leadId?: string;
  saleReference: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  propertyTitle: string;
  propertyAddress: string;
  saleAmount: number;
  saleDate: string;
  commissionRate: number;
  currency?: string;
  type?: 'sale' | 'rental' | 'referral' | 'bonus' | 'other';
  dueDate?: string;
  notes?: string;
}

export interface CommissionUpdateRequest {
  status?: 'pending' | 'partial' | 'paid' | 'cancelled' | 'overdue';
  paidAt?: string;
  paymentMethod?: string;
  notes?: string;
}

export interface CommissionFilters {
  search?: string;
  agentId?: string;
  status?: 'pending' | 'partial' | 'paid' | 'cancelled' | 'overdue';
  type?: 'sale' | 'rental' | 'referral' | 'bonus' | 'other';
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  page?: number;
  limit?: number;
}

export interface CommissionStats {
  totalCommissions: number;
  totalAmount: number;
  activeAgents: number;
  paidCommissions: number;
  paidAmount: number;
  pendingCommissions: number;
  pendingAmount: number;
  partialCommissions: number;
  partialAmount: number;
}

export interface CommissionListResponse {
  data: Commission[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CommissionPaymentRequest {
  amount: number;
  type: 'advance' | 'partial' | 'final';
  date: string;
  paymentMethod: string;
  reference: string;
  notes?: string;
}

class CommissionsApiService {
  // Obtener todas las comisiones con filtros
  async getCommissions(
    filters: CommissionFilters = {}
  ): Promise<CommissionListResponse> {
    const response = await commissionsApi.get('/', { params: filters });
    return response.data;
  }

  // Obtener comisión por ID
  async getCommission(id: string): Promise<Commission> {
    const response = await commissionsApi.get(`/${id}`);
    return response.data;
  }

  // Crear nueva comisión
  async createCommission(
    commission: CommissionCreateRequest
  ): Promise<Commission> {
    const response = await commissionsApi.post('/', commission);
    return response.data;
  }

  // Actualizar comisión
  async updateCommission(
    id: string,
    updates: CommissionUpdateRequest
  ): Promise<Commission> {
    const response = await commissionsApi.patch(`/${id}`, updates);
    return response.data;
  }

  // Eliminar comisión
  async deleteCommission(id: string): Promise<void> {
    await commissionsApi.delete(`/${id}`);
  }

  // Obtener estadísticas de comisiones
  async getCommissionStats(): Promise<CommissionStats> {
    const response = await commissionsApi.get('/stats');
    return response.data;
  }

  // Agregar pago a comisión
  async addPayment(
    commissionId: string,
    payment: CommissionPaymentRequest
  ): Promise<CommissionPayment> {
    const response = await commissionsApi.post(
      `/${commissionId}/payments`,
      payment
    );
    return response.data;
  }

  // Cambiar estado de comisión
  async updateStatus(
    commissionId: string,
    status: string
  ): Promise<Commission> {
    const response = await commissionsApi.patch(
      `/${commissionId}/status/${status}`
    );
    return response.data;
  }

  // Autorizar pago de comisión
  async authorizePayment(commissionId: string): Promise<Commission> {
    return this.updateStatus(commissionId, 'paid');
  }

  // Marcar como parcial
  async markAsPartial(commissionId: string): Promise<Commission> {
    return this.updateStatus(commissionId, 'partial');
  }

  // Cancelar comisión
  async cancelCommission(commissionId: string): Promise<Commission> {
    return this.updateStatus(commissionId, 'cancelled');
  }

  // Generar recibo (placeholder - implementar cuando esté disponible)
  async generateReceipt(commissionId: string): Promise<{ url: string }> {
    // TODO: Implementar cuando el endpoint esté disponible
    throw new Error('Generación de recibo no implementada aún');
  }

  // Descargar comisión (placeholder - implementar cuando esté disponible)
  async downloadCommission(commissionId: string): Promise<{ url: string }> {
    // TODO: Implementar cuando el endpoint esté disponible
    throw new Error('Descarga de comisión no implementada aún');
  }

  // Enviar por correo (placeholder - implementar cuando esté disponible)
  async sendByEmail(commissionId: string, email: string): Promise<void> {
    // TODO: Implementar cuando el endpoint esté disponible
    throw new Error('Envío por correo no implementado aún');
  }
}

export const commissionsApiService = new CommissionsApiService();
export default commissionsApiService;
